import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { log } from "../lib/log";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      setSavedIds(new Set());
      return;
    }
    try {
      const list = await api.listWishlist();
      setItems(list);
      setSavedIds(new Set(list.map((d) => d.savedId)));
    } catch (err) {
      log.error("Failed to load wishlist", err);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSaved = useCallback((id) => savedIds.has(String(id)), [savedIds]);

  const toggle = useCallback(
    async (deal) => {
      if (!user) return { needAuth: true };
      const id = String(deal.id);
      if (savedIds.has(id)) {
        await api.removeWishlist(id);
        setSavedIds((s) => {
          const n = new Set(s);
          n.delete(id);
          return n;
        });
        setItems((it) => it.filter((d) => d.savedId !== id));
        return { removed: true };
      }
      await api.addWishlist(deal);
      setSavedIds((s) => new Set(s).add(id));
      setItems((it) => [{ ...deal, savedId: id }, ...it]);
      return { added: true };
    },
    [user, savedIds]
  );

  const value = useMemo(() => ({ items, isSaved, toggle, refresh }), [items, isSaved, toggle, refresh]);

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export const useWishlist = () => useContext(WishlistContext);
